import { useState, useEffect } from "react";
import { 
  ClipboardList, 
  Camera, 
  FileWarning, 
  Clock, 
  DollarSign,
  Mail,
  TrendingUp,
  Users,
  Gift,
  Activity,
  Check,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  CheckCircle,
  Send,
  Eye,
  Edit,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sub-components for each feature
const MaterialsChecklist = ({ projectId, token }) => {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchChecklist();
  }, [projectId]);

  const fetchChecklist = async () => {
    try {
      const res = await axios.get(`${API}/owner/project/${projectId}/checklist`, { headers });
      setChecklist(res.data.items || []);
    } catch (err) {
      console.error("Error fetching checklist:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (index) => {
    const updated = [...checklist];
    updated[index].status = updated[index].status === "complete" ? "pending" : "complete";
    setChecklist(updated);
    
    try {
      await axios.put(`${API}/owner/project/${projectId}/checklist`, { items: updated }, { headers });
    } catch (err) {
      toast.error("Failed to update checklist");
    }
  };

  const addItem = async () => {
    if (!newItem.trim()) return;
    
    try {
      await axios.post(`${API}/owner/project/${projectId}/checklist/item`, 
        { name: newItem, status: "pending" }, { headers });
      setChecklist([...checklist, { name: newItem, status: "pending" }]);
      setNewItem("");
      toast.success("Item added");
    } catch (err) {
      toast.error("Failed to add item");
    }
  };

  if (loading) return <div className="animate-pulse p-4">Loading checklist...</div>;

  const completedCount = checklist.filter(i => i.status === "complete").length;
  const progress = checklist.length > 0 ? Math.round((completedCount / checklist.length) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-sm text-gray-500">{completedCount}/{checklist.length}</span>
        </div>
      </div>

      <div className="space-y-2">
        {checklist.map((item, idx) => (
          <div 
            key={idx}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              item.status === "complete" ? "bg-green-50 border-green-200" : "bg-white border-gray-200 hover:border-gray-300"
            }`}
            onClick={() => toggleItem(idx)}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              item.status === "complete" ? "bg-green-500 border-green-500" : "border-gray-300"
            }`}>
              {item.status === "complete" && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className={item.status === "complete" ? "line-through text-gray-400" : "text-gray-700"}>
              {item.name}
            </span>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add custom item..."
          className="flex-1"
          onKeyPress={(e) => e.key === "Enter" && addItem()}
        />
        <Button onClick={addItem} size="sm">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const PhotoTimeline = ({ projectId, token }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPhotos();
  }, [projectId]);

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/owner/project/${projectId}/photos`, { headers });
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error("Error fetching photos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        await axios.post(`${API}/owner/project/${projectId}/photos`, {
          photo_data: reader.result,
          description: description || "Progress photo"
        }, { headers });
        toast.success("Photo uploaded");
        setDescription("");
        fetchPhotos();
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="animate-pulse p-4">Loading photos...</div>;

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      <div className="flex gap-2">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Photo description..."
          className="flex-1"
        />
        <label className="cursor-pointer">
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          <Button disabled={uploading} asChild>
            <span><Camera className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload"}</span>
          </Button>
        </label>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {photos.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No photos uploaded yet</p>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            {photos.map((photo, idx) => (
              <div key={photo.id} className="relative pl-10 pb-6">
                <div className="absolute left-2.5 w-3 h-3 bg-primary rounded-full border-2 border-white" />
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <img 
                    src={photo.photo_data} 
                    alt={photo.description}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-3">
                    <p className="text-sm font-medium">{photo.description || "Progress photo"}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(photo.created_at).toLocaleString()} • {photo.uploaded_by_name || photo.uploaded_by}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const JobDurationTracker = ({ projectId, token }) => {
  const [tracking, setTracking] = useState({ status: "not_started", total_minutes: 0 });
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchTracking();
  }, [projectId]);

  const fetchTracking = async () => {
    try {
      const res = await axios.get(`${API}/owner/project/${projectId}/job-tracking`, { headers });
      setTracking(res.data);
    } catch (err) {
      console.error("Error fetching job tracking:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (action) => {
    try {
      const res = await axios.post(`${API}/owner/project/${projectId}/job-status`, { action }, { headers });
      setTracking(prev => ({
        ...prev,
        status: res.data.status,
        total_minutes: res.data.total_minutes,
        total_hours: res.data.total_hours
      }));
      toast.success(`Job ${action}`);
    } catch (err) {
      toast.error(`Failed to ${action} job`);
    }
  };

  if (loading) return <div className="animate-pulse p-4">Loading...</div>;

  const hours = Math.floor(tracking.total_minutes / 60);
  const minutes = Math.round(tracking.total_minutes % 60);

  const statusColors = {
    not_started: "bg-gray-100 text-gray-700",
    in_progress: "bg-blue-100 text-blue-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-green-100 text-green-700"
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[tracking.status]}`}>
          {tracking.status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
        </span>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900">{hours}h {minutes}m</p>
          <p className="text-xs text-gray-500">Total time</p>
        </div>
      </div>

      <div className="flex gap-2">
        {tracking.status === "not_started" && (
          <Button onClick={() => updateStatus("start")} className="flex-1 bg-green-600 hover:bg-green-700">
            <Play className="w-4 h-4 mr-2" />Start Job
          </Button>
        )}
        {tracking.status === "in_progress" && (
          <>
            <Button onClick={() => updateStatus("pause")} variant="outline" className="flex-1">
              <Pause className="w-4 h-4 mr-2" />Pause
            </Button>
            <Button onClick={() => updateStatus("complete")} className="flex-1 bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />Complete
            </Button>
          </>
        )}
        {tracking.status === "paused" && (
          <>
            <Button onClick={() => updateStatus("start")} className="flex-1">
              <Play className="w-4 h-4 mr-2" />Resume
            </Button>
            <Button onClick={() => updateStatus("complete")} variant="outline" className="flex-1">
              <CheckCircle className="w-4 h-4 mr-2" />Complete
            </Button>
          </>
        )}
        {tracking.status === "completed" && (
          <div className="w-full text-center py-3 bg-green-50 rounded-lg text-green-700 font-medium">
            Job Completed
          </div>
        )}
      </div>

      {tracking.time_entries && tracking.time_entries.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm font-medium text-gray-700 mb-2">Time Log</p>
          <div className="space-y-1">
            {tracking.time_entries.slice(-5).map((entry, idx) => (
              <div key={idx} className="flex justify-between text-xs text-gray-500">
                <span>{new Date(entry.start).toLocaleString()}</span>
                <span>{entry.duration_minutes ? `${Math.round(entry.duration_minutes)}m` : "In progress"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const VariationsPanel = ({ projectId, token }) => {
  const [variations, setVariations] = useState([]);
  const [totals, setTotals] = useState({ approved: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newVariation, setNewVariation] = useState({ description: "", estimated_cost: "", notes: "" });
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchVariations();
  }, [projectId]);

  const fetchVariations = async () => {
    try {
      const res = await axios.get(`${API}/owner/project/${projectId}/variations`, { headers });
      setVariations(res.data.variations || []);
      setTotals(res.data.totals || { approved: 0, pending: 0 });
    } catch (err) {
      console.error("Error fetching variations:", err);
    } finally {
      setLoading(false);
    }
  };

  const submitVariation = async () => {
    if (!newVariation.description || !newVariation.estimated_cost) {
      toast.error("Please fill in description and cost");
      return;
    }

    try {
      await axios.post(`${API}/owner/project/${projectId}/variations`, {
        ...newVariation,
        estimated_cost: parseFloat(newVariation.estimated_cost)
      }, { headers });
      toast.success("Variation submitted");
      setShowForm(false);
      setNewVariation({ description: "", estimated_cost: "", notes: "" });
      fetchVariations();
    } catch (err) {
      toast.error("Failed to submit variation");
    }
  };

  const approveVariation = async (variationId, approved, finalCost = null) => {
    try {
      await axios.put(`${API}/owner/project/${projectId}/variations/${variationId}`, {
        approved,
        final_cost: finalCost
      }, { headers });
      toast.success(approved ? "Variation approved" : "Variation rejected");
      fetchVariations();
    } catch (err) {
      toast.error("Failed to update variation");
    }
  };

  if (loading) return <div className="animate-pulse p-4">Loading variations...</div>;

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700"
  };

  return (
    <div className="space-y-4">
      {/* Totals */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-green-50 rounded-lg">
          <p className="text-xs text-green-600">Approved</p>
          <p className="text-xl font-bold text-green-700">£{totals.approved.toLocaleString()}</p>
        </div>
        <div className="p-3 bg-yellow-50 rounded-lg">
          <p className="text-xs text-yellow-600">Pending</p>
          <p className="text-xl font-bold text-yellow-700">£{totals.pending.toLocaleString()}</p>
        </div>
      </div>

      {/* Variations List */}
      <div className="space-y-3">
        {variations.length === 0 ? (
          <p className="text-center text-gray-400 py-4">No variations yet</p>
        ) : (
          variations.map((v) => (
            <div key={v.id} className="p-4 border rounded-lg bg-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{v.description}</p>
                  {v.notes && <p className="text-sm text-gray-500 mt-1">{v.notes}</p>}
                  <p className="text-xs text-gray-400 mt-2">
                    Submitted: {new Date(v.submitted_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[v.status]}`}>
                    {v.status}
                  </span>
                  <p className="text-lg font-bold text-gray-900 mt-2">
                    £{(v.final_cost || v.estimated_cost).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {v.status === "pending" && (
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button 
                    onClick={() => approveVariation(v.id, true)} 
                    size="sm" 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-1" />Approve
                  </Button>
                  <Button 
                    onClick={() => approveVariation(v.id, false)} 
                    size="sm" 
                    variant="outline"
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-1" />Reject
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Variation Form */}
      {showForm ? (
        <div className="p-4 border-2 border-dashed border-primary rounded-lg space-y-3">
          <Input
            value={newVariation.description}
            onChange={(e) => setNewVariation({ ...newVariation, description: e.target.value })}
            placeholder="Variation description"
          />
          <Input
            type="number"
            value={newVariation.estimated_cost}
            onChange={(e) => setNewVariation({ ...newVariation, estimated_cost: e.target.value })}
            placeholder="Estimated cost (£)"
          />
          <Textarea
            value={newVariation.notes}
            onChange={(e) => setNewVariation({ ...newVariation, notes: e.target.value })}
            placeholder="Additional notes..."
            rows={2}
          />
          <div className="flex gap-2">
            <Button onClick={submitVariation} className="flex-1">Submit</Button>
            <Button onClick={() => setShowForm(false)} variant="outline">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setShowForm(true)} variant="outline" className="w-full">
          <Plus className="w-4 h-4 mr-2" />Add Variation
        </Button>
      )}
    </div>
  );
};

const ActivityLog = ({ projectId, token }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchActivities();
  }, [projectId]);

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API}/owner/project/${projectId}/activity`, { headers });
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error("Error fetching activities:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="animate-pulse p-4">Loading...</div>;

  const getIcon = (action) => {
    if (action.includes("photo")) return <Camera className="w-4 h-4" />;
    if (action.includes("variation")) return <FileWarning className="w-4 h-4" />;
    if (action.includes("job")) return <Clock className="w-4 h-4" />;
    if (action.includes("payment")) return <DollarSign className="w-4 h-4" />;
    if (action.includes("checklist")) return <ClipboardList className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getColor = (action) => {
    if (action.includes("complete") || action.includes("approved")) return "text-green-600 bg-green-100";
    if (action.includes("rejected")) return "text-red-600 bg-red-100";
    if (action.includes("start")) return "text-blue-600 bg-blue-100";
    return "text-gray-600 bg-gray-100";
  };

  return (
    <div className="space-y-2">
      {activities.length === 0 ? (
        <p className="text-center text-gray-400 py-8">No activity recorded yet</p>
      ) : (
        activities.map((a) => (
          <div key={a.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border">
            <div className={`p-2 rounded-full ${getColor(a.action)}`}>
              {getIcon(a.action)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {a.action.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
              </p>
              <p className="text-xs text-gray-500 truncate">{a.details}</p>
              <p className="text-xs text-gray-400 mt-1">
                {new Date(a.created_at).toLocaleString()} • {a.performed_by}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Main OperationsPanel Component
const OperationsPanel = ({ token }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("checklist");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      // Get leads with job_won or in_progress status
      const res = await axios.get(`${API}/owner/leads`, { headers });
      const activeProjects = res.data.filter(l => 
        ["job_won", "in_progress", "scheduled", "job_completed"].includes(l.status) ||
        ["job_won", "in_progress", "scheduled", "job_completed"].includes(l.pipeline_stage)
      );
      setProjects(activeProjects);
      if (activeProjects.length > 0) {
        setSelectedProject(activeProjects[0]);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No Active Projects</h3>
          <p className="text-gray-400">
            Move a lead to "Job Won" or "In Progress" to start tracking operations.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "checklist", label: "Materials", icon: ClipboardList },
    { id: "photos", label: "Photos", icon: Camera },
    { id: "duration", label: "Duration", icon: Clock },
    { id: "variations", label: "Variations", icon: FileWarning },
    { id: "activity", label: "Activity", icon: Activity }
  ];

  return (
    <div className="p-6" data-testid="operations-panel">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Project Operations</h1>
        <p className="text-gray-600">Manage materials, track progress, and log variations</p>
      </div>

      {/* Project Selector */}
      <div className="mb-6">
        <Label>Select Project</Label>
        <select
          value={selectedProject?.id || ""}
          onChange={(e) => setSelectedProject(projects.find(p => p.id === e.target.value))}
          className="w-full mt-1 p-3 border rounded-lg bg-white"
        >
          {projects.map(p => (
            <option key={p.id} value={p.id}>
              {p.customer_name || p.name} - {p.project_type} ({p.status})
            </option>
          ))}
        </select>
      </div>

      {selectedProject && (
        <>
          {/* Project Summary */}
          <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">{selectedProject.customer_name || selectedProject.name}</h2>
                <p className="text-sm text-gray-500">{selectedProject.project_type} • {selectedProject.postcode}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-primary">
                  £{(selectedProject.job_value || selectedProject.estimate_high || 0).toLocaleString()}
                </p>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  selectedProject.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                  selectedProject.status === "job_completed" ? "bg-green-100 text-green-700" :
                  "bg-yellow-100 text-yellow-700"
                }`}>
                  {selectedProject.status?.replace(/_/g, " ")}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? "bg-primary text-white" 
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {activeTab === "checklist" && (
              <MaterialsChecklist projectId={selectedProject.id} token={token} />
            )}
            {activeTab === "photos" && (
              <PhotoTimeline projectId={selectedProject.id} token={token} />
            )}
            {activeTab === "duration" && (
              <JobDurationTracker projectId={selectedProject.id} token={token} />
            )}
            {activeTab === "variations" && (
              <VariationsPanel projectId={selectedProject.id} token={token} />
            )}
            {activeTab === "activity" && (
              <ActivityLog projectId={selectedProject.id} token={token} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OperationsPanel;
